import {
  PlatformActionName,
  CreateReminderActionPayloadSchema,
  CreateReminderActionResultSchema,
  FetchRemindersActionPayloadSchema,
  FetchRemindersActionResultSchema,
  FindReminderActionPayloadSchema,
  FindReminderActionResultSchema,
  UpdateReminderActionPayloadSchema,
  UpdateReminderActionResultSchema,
  CompleteReminderActionPayloadSchema,
  CompleteReminderActionResultSchema,
  DeleteReminderActionPayloadSchema,
  DeleteReminderActionResultSchema,
  FetchContactsActionPayloadSchema,
  FetchContactsActionResultSchema,
  FindContactActionPayloadSchema,
  FindContactActionResultSchema,
  CreateContactActionPayloadSchema,
  CreateContactActionResultSchema,
  UpdateContactActionPayloadSchema,
  UpdateContactActionResultSchema,
  DeleteContactActionPayloadSchema,
  DeleteContactActionResultSchema,
  type CreateReminderActionPayload,
  type CreateReminderActionResult,
  type FetchRemindersActionPayload,
  type FetchRemindersActionResult,
  type FindReminderActionPayload,
  type FindReminderActionResult,
  type UpdateReminderActionPayload,
  type UpdateReminderActionResult,
  type CompleteReminderActionPayload,
  type CompleteReminderActionResult,
  type DeleteReminderActionPayload,
  type DeleteReminderActionResult,
  type FetchContactsActionPayload,
  type FetchContactsActionResult,
  type FindContactActionPayload,
  type FindContactActionResult,
  type CreateContactActionPayload,
  type CreateContactActionResult,
  type UpdateContactActionPayload,
  type UpdateContactActionResult,
  type DeleteContactActionPayload,
  type DeleteContactActionResult,
} from '@electron/shared/platform-actions.types';

import { ElectronAPI } from '../base/ElectronAPI';
import { IPCEventType } from '../base/types';

import type { ZodType } from 'zod';

export class PlatformActionError extends Error {
  constructor(
    public readonly action: PlatformActionName,
    reason?: string
  ) {
    super(reason ?? `Platform action ${action} failed`);
    this.name = 'PlatformActionError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class PlatformActionsAPI extends ElectronAPI {
  async createReminder(
    payload: CreateReminderActionPayload
  ): Promise<CreateReminderActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.CreateReminder,
      CreateReminderActionPayloadSchema.parse(payload),
      CreateReminderActionResultSchema
    );
  }

  async fetchReminders(
    payload: FetchRemindersActionPayload
  ): Promise<FetchRemindersActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.FetchReminders,
      FetchRemindersActionPayloadSchema.parse(payload),
      FetchRemindersActionResultSchema
    );
  }

  async findReminder(
    payload: FindReminderActionPayload
  ): Promise<FindReminderActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.FindReminder,
      FindReminderActionPayloadSchema.parse(payload),
      FindReminderActionResultSchema
    );
  }

  async updateReminder(
    payload: UpdateReminderActionPayload
  ): Promise<UpdateReminderActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.UpdateReminder,
      UpdateReminderActionPayloadSchema.parse(payload),
      UpdateReminderActionResultSchema
    );
  }

  async completeReminder(
    payload: CompleteReminderActionPayload
  ): Promise<CompleteReminderActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.CompleteReminder,
      CompleteReminderActionPayloadSchema.parse(payload),
      CompleteReminderActionResultSchema
    );
  }

  async deleteReminder(
    payload: DeleteReminderActionPayload
  ): Promise<DeleteReminderActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.DeleteReminder,
      DeleteReminderActionPayloadSchema.parse(payload),
      DeleteReminderActionResultSchema
    );
  }

  async fetchContacts(
    payload: FetchContactsActionPayload
  ): Promise<FetchContactsActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.FetchContacts,
      FetchContactsActionPayloadSchema.parse(payload),
      FetchContactsActionResultSchema
    );
  }

  async findContact(
    payload: FindContactActionPayload
  ): Promise<FindContactActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.FindContact,
      FindContactActionPayloadSchema.parse(payload),
      FindContactActionResultSchema
    );
  }

  async createContact(
    payload: CreateContactActionPayload
  ): Promise<CreateContactActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.CreateContact,
      CreateContactActionPayloadSchema.parse(payload),
      CreateContactActionResultSchema
    );
  }

  async updateContact(
    payload: UpdateContactActionPayload
  ): Promise<UpdateContactActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.UpdateContact,
      UpdateContactActionPayloadSchema.parse(payload),
      UpdateContactActionResultSchema
    );
  }

  async deleteContact(
    payload: DeleteContactActionPayload
  ): Promise<DeleteContactActionResult> {
    return this.invokePlatformAction(
      PlatformActionName.DeleteContact,
      DeleteContactActionPayloadSchema.parse(payload),
      DeleteContactActionResultSchema
    );
  }

  private async invokePlatformAction<TResult>(
    action: PlatformActionName,
    args: unknown,
    resultSchema: ZodType<TResult>
  ): Promise<TResult> {
    const response = await this.requestContextKit(
      IPCEventType.invokePlatformAction,
      {
        action,
        arguments: JSON.stringify(args),
      }
    );

    if (response?.type === 'error') {
      throw new PlatformActionError(action, response.info ?? undefined);
    }
    if (!response?.info) {
      throw new PlatformActionError(action, 'Empty response from ContextKit');
    }

    try {
      return resultSchema.parse(JSON.parse(response.info));
    } catch {
      throw new PlatformActionError(action, 'Invalid result from ContextKit');
    }
  }
}
